import { fft, ifft, nextPow2 } from './fft.js';

export interface EnvelopeAnalysis {
  /** |analytic signal|, same length as input. */
  envelope: Float32Array;
  /**
   * Imaginary part of the analytic signal actually used (either the supplied
   * `corrImag` or the internally computed Hilbert transform). Lets callers
   * compute phase coherence at a different index via
   * {@link phaseCoherenceAtIndex} without re-deriving it.
   */
  analyticImag: Float32Array;
  /** argmax of envelope. -1 if input length is 0. */
  peakIndex: number;
  /** envelope[peakIndex]. 0 if input length is 0. */
  peakValue: number;
  /** 0..1, higher = cleaner peak. 0 if peak/window is degenerate. */
  phaseCoherence: number;
}

export interface ComputeEnvelopeAnalysisArgs {
  corrReal: Float32Array;
  /** Optional pre-computed analytic-signal imaginary part. If non-null and not all zero, used directly. */
  corrImag?: Float32Array | null;
  /** Half-width (in samples) of the window around the peak used for phase coherence. Default 8. */
  coherenceWindowSamples?: number;
}

/**
 * Standalone Hilbert transform via FFT.
 *
 * Returns the imaginary part of the analytic signal, i.e. an approximation of the
 * 90-degree phase-shifted version of `real`. Same length as input.
 *
 * Algorithm:
 *   1. Zero-pad to next power of two, FFT.
 *   2. Multiply positive-frequency bins (0 < k < N/2) by 2, zero negative-frequency
 *      bins (N/2 < k < N), leave DC and Nyquist unchanged. This produces the analytic
 *      signal in the frequency domain.
 *   3. IFFT, extract imaginary part, truncate back to original length.
 */
export function hilbertTransform(real: Float32Array): Float32Array {
  const L = real.length;
  if (L === 0) return new Float32Array(0);
  if (L === 1) return new Float32Array([0]);

  const N = nextPow2(L);
  const re = new Float32Array(N);
  const im = new Float32Array(N);
  re.set(real);

  fft(re, im);

  const half = N >>> 1;
  // Positive frequencies (excluding DC and Nyquist): multiply by 2
  for (let k = 1; k < half; k++) {
    re[k] *= 2;
    im[k] *= 2;
  }
  // Negative frequencies: zero out
  for (let k = half + 1; k < N; k++) {
    re[k] = 0;
    im[k] = 0;
  }
  // DC (k=0) and Nyquist (k=half) untouched.

  ifft(re, im);

  // Imaginary part of the analytic signal is the Hilbert transform.
  const out = new Float32Array(L);
  for (let i = 0; i < L; i++) out[i] = im[i];
  return out;
}

/**
 * Cheap probabilistic "is this array effectively all zero?" check.
 *
 * The naive O(N) full pass walked the entire correlation buffer (typically
 * 100k+ samples) on every ping just to decide whether to bypass the
 * Hilbert path. We only need this to catch the common case of an
 * explicitly zero-initialized buffer (`new Float32Array(N)`) — a real
 * analytic-signal imag part has nonzero samples densely.
 *
 * Strategy:
 *   1. Sample 16 evenly-spaced positions. Any nonzero short-circuits.
 *   2. If sampling looks all-zero, do one cheap confirm pass over the
 *      first 64 samples (catches sparse populated arrays whose nonzeros
 *      happen near the start).
 *
 * False negatives (missing a single isolated nonzero outside both probe
 * regions) are possible but harmless: the only consequence is computing
 * an extra Hilbert pass — same as before the optimization.
 */
function isAllZero(arr: Float32Array | null | undefined): boolean {
  if (!arr || arr.length === 0) return true;
  const N = arr.length;
  // Stride sample across the whole array.
  const samples = Math.min(16, N);
  for (let i = 0; i < samples; i++) {
    const idx = Math.floor((i / samples) * N);
    if (arr[idx] !== 0) return false;
  }
  // Confirm pass on the first 64 samples (or all if shorter).
  const confirmEnd = Math.min(64, N);
  for (let i = 0; i < confirmEnd; i++) {
    if (arr[i] !== 0) return false;
  }
  return true;
}

/**
 * Compute phase coherence of the analytic signal around `index`, looking at
 * a window of `windowHalf` samples on each side.
 *
 * For a clean tone-like echo the instantaneous phase advances at a constant
 * rate (~ -omega0 * dt per sample); for noise the first differences are
 * essentially uniform on [-pi, pi]. Coherence = |mean(e^{i*diff})| of the
 * wrapped first differences, in [0, 1] (1 = perfectly constant phase rate).
 *
 * Wrapped phases suffice: unwrapping only adds multiples of 2*pi, which
 * vanish when the differences are re-wrapped — so this computes atan2 only
 * over the window instead of the whole array.
 */
export function phaseCoherenceAtIndex(
  corrReal: Float32Array,
  imag: Float32Array,
  index: number,
  windowHalf = 8,
): number {
  const N = Math.min(corrReal.length, imag.length);
  if (N < 2 || index < 0 || index >= N) return 0;
  const start = Math.max(0, index - windowHalf);
  const end = Math.min(N - 1, index + windowHalf);
  if (end - start < 2) return 0;

  let sumCos = 0;
  let sumSin = 0;
  let count = 0;
  let prev = Math.atan2(imag[start], corrReal[start]);
  for (let i = start + 1; i <= end; i++) {
    const ph = Math.atan2(imag[i], corrReal[i]);
    let d = ph - prev;
    prev = ph;
    // Wrap to [-pi, pi]
    d = ((d + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
    sumCos += Math.cos(d);
    sumSin += Math.sin(d);
    count++;
  }
  if (count === 0) return 0;
  const meanCos = sumCos / count;
  const meanSin = sumSin / count;
  const r = Math.sqrt(meanCos * meanCos + meanSin * meanSin);
  if (!Number.isFinite(r)) return 0;
  if (r < 0) return 0;
  if (r > 1) return 1;
  return r;
}

/**
 * Compute envelope, peak location, and phase-coherence quality metric from a
 * (possibly complex) cross-correlation output.
 *
 * If `corrImag` is provided and not all zeros, it is treated as the imaginary part
 * of the already-analytic signal (e.g. from `fftCorrelateComplex`). Otherwise the
 * Hilbert transform of `corrReal` is computed via FFT.
 */
export function computeEnvelopeAnalysis(args: ComputeEnvelopeAnalysisArgs): EnvelopeAnalysis {
  const { corrReal } = args;
  const windowHalf = args.coherenceWindowSamples ?? 8;
  const N = corrReal.length;

  if (N === 0) {
    return {
      envelope: new Float32Array(0),
      analyticImag: new Float32Array(0),
      peakIndex: -1,
      peakValue: 0,
      phaseCoherence: 0,
    };
  }

  // Determine imaginary part of analytic signal.
  let imag: Float32Array;
  if (args.corrImag && args.corrImag.length === N && !isAllZero(args.corrImag)) {
    imag = args.corrImag;
  } else {
    imag = hilbertTransform(corrReal);
  }

  // Envelope = |analytic signal|.
  const envelope = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const re = corrReal[i];
    const im = imag[i];
    envelope[i] = Math.sqrt(re * re + im * im);
  }

  // Peak.
  let peakIndex = 0;
  let peakValue = envelope[0];
  for (let i = 1; i < N; i++) {
    if (envelope[i] > peakValue) {
      peakValue = envelope[i];
      peakIndex = i;
    }
  }

  // Phase coherence is meaningless if the signal is essentially zero — return 0.
  let phaseCoherence = 0;
  if (peakValue > 0 && Number.isFinite(peakValue)) {
    phaseCoherence = phaseCoherenceAtIndex(corrReal, imag, peakIndex, windowHalf);
  }

  return {
    envelope,
    analyticImag: imag,
    peakIndex,
    peakValue,
    phaseCoherence,
  };
}
